import {
	ActionMap,
	FormContext_Action_Payload,
	FormContext_Actions,
} from '@/components/forms/FormContext';
import { RepairFormState, StepMap } from '../../constants';
import { v4 } from 'uuid';
import { HubspotFile } from '@/helpers/hubspot/types';
import { collateImages } from './components/UploadPhoto/helpers/collateImages';

export interface ImageFile {
	id: string;
	image: File | null;
	name: string;
	inProgress: boolean;
	uploadBatchId: string | null;
	error: string | null;
}

export const PictureStepActions = {
	...FormContext_Actions,
	ADD_PICTURE: 'ADD_PICTURE',
	REMOVE_PICTURE: 'REMOVE_PICTURE',
	UPDATE_MODEL_NUMBER: 'UPDATE_MODEL_NUMBER',
	UPDATE_SYSTEM_LOCATION: 'UPDATE_SYSTEM_LOCATION',
	UPDATE_MODEL_TYPE: 'UPDATE_MODEL_TYPE',
	UPDATE_MODEL_SIZE: 'UPDATE_MODEL_SIZE',
	START_IMAGE_UPLOAD: 'START_IMAGE_UPLOAD',
	END_HUBSPOT_FILE_UPLOAD: 'END_HUBSPOT_FILE_UPLOAD',
	SET_HUBSPOT_FOLDER_ID: 'SET_HUBSPOT_FOLDER_ID',
} as const;

export type PictureStepAction = keyof typeof PictureStepActions;

export interface PictureStepActionPayload
	extends FormContext_Action_Payload<StepMap> {
	[PictureStepActions.ADD_PICTURE]: ImageFile;
	[PictureStepActions.REMOVE_PICTURE]: ImageFile;
	[PictureStepActions.UPDATE_MODEL_NUMBER]: string;
	[PictureStepActions.UPDATE_MODEL_TYPE]: string;
	[PictureStepActions.UPDATE_SYSTEM_LOCATION]: string;
	[PictureStepActions.UPDATE_MODEL_SIZE]: string;
	[PictureStepActions.START_IMAGE_UPLOAD]: {
		uploadBatchId: string;
		file: File;
	}[];

	[PictureStepActions.END_HUBSPOT_FILE_UPLOAD]: {
		files: HubspotFile[];
		uploadBatchId: string;
	};
	[PictureStepActions.SET_REJECTED]: null;
	[PictureStepActions.SET_HUBSPOT_FOLDER_ID]: { hubspotFolderId: string };
}

export type PictureStepActionMap =
	ActionMap<PictureStepActionPayload>[keyof ActionMap<PictureStepActionPayload>];

export const isPictureStepActionMap = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	action: any,
): action is PictureStepActionMap => action.type in PictureStepActions;

export const pictureStepReducer = (
	state: RepairFormState,
	action: PictureStepActionMap,
): RepairFormState => {
	switch (action.type) {
		case PictureStepActions.ADD_PICTURE:
			return {
				...state,
				pictures: [...state.pictures, action.payload],
			};
		case PictureStepActions.REMOVE_PICTURE:
			return {
				...state,
				pictures: state.pictures.filter(
					(picture) => picture.id !== action.payload.id,
				),
			};
		case PictureStepActions.UPDATE_MODEL_NUMBER:
			return {
				...state,
				modelNumber: action.payload,
			};
		case PictureStepActions.UPDATE_MODEL_TYPE:
			return {
				...state,
				modelType: action.payload,
			};
		case PictureStepActions.UPDATE_SYSTEM_LOCATION:
			return {
				...state,
				systemLocation: action.payload,
			};
		case PictureStepActions.UPDATE_MODEL_SIZE:
			return {
				...state,
				modelSize: action.payload,
			};
		case PictureStepActions.START_IMAGE_UPLOAD:
			return {
				...state,
				pictures: [
					...state.pictures,
					...action.payload.map(
						(file): ImageFile => ({
							id: v4(),
							image: file.file,
							name: file.file.name ?? 'image',
							inProgress: true,
							uploadBatchId: file.uploadBatchId,
							error: null,
						}),
					),
				],
			};
		case PictureStepActions.END_HUBSPOT_FILE_UPLOAD:
			return {
				...state,
				pictures: collateImages(state.pictures, action.payload),
			};
		case PictureStepActions.SET_REJECTED:
			return {
				...state,
				requestStatus: 'REJECTED',
			};
		case PictureStepActions.SET_HUBSPOT_FOLDER_ID:
			return {
				...state,
				hubspotFolderId: action.payload.hubspotFolderId,
			};
		default:
			return state;
	}
};

export const isValid = (state: RepairFormState): boolean => {
	const hasPictures = !!state.pictures.length;
	const hasSystemLocation = !!state.modelNumber;
	const hasTypeSizeLocation =
		!!state.modelType && !!state.modelSize && !!state.systemLocation;
	return hasPictures || hasSystemLocation || hasTypeSizeLocation;
};
